import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function HeroSection() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [displayText, setDisplayText] = useState("Selecionar Categoria");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDisplayText(cat.namePt + " - " + cat.name);
    setIsOpen(false);
    window.open(cat.url, "_blank", "noopener,nofollow");
  };

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Aesthetic%20overhead%20flat%20lay%20of%20assorted%20fashion%20items%20scattered%20on%20a%20warm%20sand%20colored%20linen%20fabric%2C%20including%20a%20pair%20of%20vintage%20white%20leather%20sneakers%2C%20round%20sunglasses%2C%20a%20silver%20chain%20necklace%2C%20a%20beige%20baseball%20cap%2C%20and%20a%20canvas%20tote%20bag%2C%20soft%20natural%20daylight%20from%20a%20large%20window%2C%20editorial%20lifestyle%20composition%2C%20airy%20and%20fresh%20atmosphere&width=1800&height=1200&seq=hero-v2-bg-01&orientation=landscape"
          alt="CSSBuy Spreadsheet produtos da China organizados"
          title="CSSBuy Spreadsheet 2026 produtos"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/35"></div>
      </div>

      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 py-24 md:py-36">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="w-full lg:w-3/5">
            <div className="inline-flex items-center gap-2 bg-background-50/90 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <div className="w-3 h-3 rounded-full bg-accent-500"></div>
              <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">CSSBuy Spreadsheet 2026</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-background-50 leading-tight mb-6">
              Planilha CSSBuy<br />
              <span className="italic font-normal">Completa 2026</span><br />
              Produtos da China
            </h1>

            <p className="text-base md:text-lg text-background-50/85 max-w-lg mb-8 leading-relaxed">
              Descubra a <strong>CSSBuy Spreadsheet</strong> mais completa com roupas, sneakers e produtos mais comprados da China com envio para Brasil e Portugal.
            </p>

            {/* Dropdown */}
            <div className="relative mb-6" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full max-w-md flex items-center justify-between bg-background-50/95 backdrop-blur-sm rounded-xl px-5 py-4 text-foreground-800 text-sm font-medium cursor-pointer transition-all hover:bg-background-50 border border-background-200/50"
              >
                <span className={selectedCategory ? "text-foreground-900" : "text-foreground-500"}>{displayText}</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}></i>
              </button>

              {isOpen && (
                <div className="absolute top-full mt-2 w-full max-w-md bg-background-50 rounded-xl shadow-lg border border-background-200/50 overflow-hidden z-50 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleSelect(cat)}
                      className="w-full text-left px-5 py-3 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors flex items-center justify-between whitespace-nowrap"
                    >
                      <span>{cat.namePt} <span className="text-foreground-400 text-xs">({cat.name})</span></span>
                      <span className="text-xs text-foreground-400">{cat.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-foreground-950 text-background-50 px-8 py-4 rounded-full text-sm font-semibold cursor-pointer transition-all hover:bg-foreground-800 whitespace-nowrap"
              >
                CSSBuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-background-50 text-sm font-medium cursor-pointer transition-all hover:opacity-80 whitespace-nowrap border-b-2 border-background-50/40 pb-1"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>

          {/* Right decorative */}
          <div className="hidden lg:block w-full lg:w-2/5">
            <p className="text-background-50/70 text-sm leading-relaxed mb-8 max-w-xs">
              A <strong>planilha CSSBuy</strong> mais completa e atualizada do mercado. Produtos verificados, preços comparados, e tudo organizado para você comprar da China com segurança e economia.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {["#E8D5C4", "#D4C4B8", "#C9B8AC"].map((color, i) => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-background-50/90" style={{ backgroundColor: color }}></div>
                ))}
              </div>
              <span className="text-xs text-background-50/70">+2.000 compradores</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom image strip */}
      <div className="absolute bottom-0 left-0 right-0 z-10 h-24 md:h-32 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Horizontal%20flat%20lay%20strip%20of%20diverse%20fashion%20accessories%20including%20sunglasses%20leather%20belt%20watch%20keychain%20and%20small%20pouch%20all%20in%20warm%20earthy%20tones%20lined%20across%20a%20textured%20stone%20surface%2C%20soft%20diffused%20daylight%2C%20minimal%20composition%2C%20clean%20product%20row&width=1800&height=200&seq=hero-v2-strip-01&orientation=landscape"
          alt="CSSBuy produtos importados"
          className="w-full h-full object-cover opacity-60"
        />
      </div>
    </section>
  );
}